/**
 * CollectionCard — a small physical stack of real covers, not a flat icon
 * tile: 2-3 actual CoverArt pieces offset diagonally by a few px each, the
 * back ones desaturated and receding, the front one crisp and lifted. Reads
 * as "this holds these covers," the way a stack of books or photos does.
 */

import React from 'react';
import { cx } from '@/components/site/primitives';
import CoverArt from './CoverArt';

const STACK_OFFSETS = [
  { x: 0, y: 0, scale: 0.94, opacity: 0.55, blur: false },
  { x: 8, y: -6, scale: 0.97, opacity: 0.8, blur: false },
  { x: 16, y: -12, scale: 1, opacity: 1, blur: false },
];

export default function CollectionCard({ collection, entries, onOpen }) {
  const preview = entries.slice(0, 3);

  return (
    <button
      type="button"
      onClick={() => onOpen(collection.key ?? collection.id)}
      data-testid={`cover-club-collection-${collection.key ?? collection.id}`}
      className="group flex flex-col overflow-visible rounded-2xl border border-line bg-card p-4 text-left shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-card"
    >
      <div className="relative h-32 w-full">
        {preview.length === 0 ? (
          <div className="absolute inset-x-0 bottom-0 mx-auto flex h-24 w-16 items-center justify-center rounded-lg border border-dashed border-line text-[10px] text-subtle">
            Empty
          </div>
        ) : (
          preview.map((entry, i) => {
            const offset = STACK_OFFSETS[STACK_OFFSETS.length - preview.length + i] ?? STACK_OFFSETS[i];
            const isFront = i === preview.length - 1;
            return (
              <div
                key={entry.bookId}
                style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${offset.scale})`, zIndex: i }}
                className={cx(
                  'absolute bottom-0 left-4 h-28 w-20 overflow-hidden rounded-lg ring-[3px] ring-card transition-transform duration-300',
                  isFront ? 'shadow-lift group-hover:-translate-y-1' : 'shadow-sm saturate-[0.55]'
                )}
              >
                <CoverArt book={entry.book} style={entry.style} className="h-full w-full" />
              </div>
            );
          })
        )}
      </div>

      <div className="mt-3">
        <p className="font-display text-base font-semibold leading-snug text-ink">{collection.title}</p>
        <p className="mt-0.5 text-xs text-subtle">{collection.curator} · {entries.length} {entries.length === 1 ? 'cover' : 'covers'}</p>
      </div>
    </button>
  );
}
